import type { Session } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import type { AppSupabaseClient } from "./supabase";

export type SessionState =
  { status: "loading" } | { status: "signed_out" } | { status: "signed_in"; session: Session };

/**
 * Error carried back by an email link (expired invitation, reused magic link). Supabase redirects with
 * `#error=...&error_description=...`; the auth client throws on it internally and nothing else surfaces it, so the
 * value is captured once at load, before the router replaces the URL.
 */
export const linkError: string | null = (() => {
  if (typeof window === "undefined") return null;
  const raw = window.location.hash.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.search.slice(1);
  const params = new URLSearchParams(raw);
  const description = params.get("error_description");
  const code = params.get("error_code");
  if (description === null && code === null) return null;
  if (code === "otp_expired")
    return "This sign-in link has expired or was already used. Request a new one below.";
  return description ?? "This sign-in link could not be used. Request a new one below.";
})();

const SessionContext = createContext<SessionState>({ status: "loading" });
const ClientContext = createContext<AppSupabaseClient | null>(null);

/** Tracks the Supabase Auth session for this origin. Authorization never derives from it; the database decides. */
export function SessionProvider({
  client,
  children,
}: {
  client: AppSupabaseClient;
  children: ReactNode;
}) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<SessionState>({ status: "loading" });
  // The signed-in person's id as last seen; undefined until the first answer.
  const userId = useRef<string | null | undefined>(undefined);
  useEffect(() => {
    let active = true;
    /*
     * Nothing cached for one person may be shown to the next (ruling R13): on sign-out, or when the session now
     * belongs to someone else, the whole query cache is dropped before the new state renders.
     */
    const observe = (session: Session | null, signedOut: boolean) => {
      const next = session?.user.id ?? null;
      const previous = userId.current;
      if (signedOut || (previous != null && previous !== next)) queryClient.clear();
      userId.current = next;
      setState(session === null ? { status: "signed_out" } : { status: "signed_in", session });
    };
    void client.auth.getSession().then(({ data }) => {
      if (!active) return;
      observe(data.session, false);
    });
    const { data: subscription } = client.auth.onAuthStateChange((event, session) => {
      observe(session, event === "SIGNED_OUT");
    });
    return () => {
      active = false;
      subscription.subscription.unsubscribe();
    };
  }, [client, queryClient]);
  return (
    <ClientContext.Provider value={client}>
      <SessionContext.Provider value={state}>{children}</SessionContext.Provider>
    </ClientContext.Provider>
  );
}

export function useSession(): SessionState {
  return useContext(SessionContext);
}

export function useSupabase(): AppSupabaseClient {
  const client = useContext(ClientContext);
  if (client === null) throw new Error("useSupabase must be used inside SessionProvider");
  return client;
}
